import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { MoreVertical, Edit2, Trash2, X, MessageSquare, ArrowRight, Reply } from 'lucide-react';
import type { Comment } from '../hooks/useComments';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import CommentForm from './CommentForm';

type CommentListProps = {
  comments: Comment[];
  onUpdateComment: (commentId: string, content: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  onAddComment: (content: string, parentId: string | null) => Promise<void>;
  canDeleteComment: (commentUserId: string) => boolean;
  ideaAuthorId: string;
  currentDiscussion?: Comment;
  isDiscussionView?: boolean;
};

export default function CommentList({ 
  comments, 
  onUpdateComment, 
  onDeleteComment,
  onAddComment,
  canDeleteComment,
  ideaAuthorId,
  currentDiscussion,
  isDiscussionView = false
}: CommentListProps) {
  const { user } = useAuth();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleEdit = (comment: Comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
    setMenuOpenId(null);
  };

  const handleSave = async (commentId: string) => {
    await onUpdateComment(commentId, editContent);
    setEditingId(null);
    setEditContent('');
  };

  const handleDelete = async (commentId: string) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        setError(null);
        await onDeleteComment(commentId);
        setMenuOpenId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete comment');
      }
    }
  };

  const displayComments = isDiscussionView && currentDiscussion 
    ? [currentDiscussion, ...(currentDiscussion.replies || [])]
    : comments;

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          {error}
        </div>
      )}
      
      {displayComments.map((comment) => {
        const isAuthorComment = comment.user.id === ideaAuthorId;
        const hasReplies = comment.replies && comment.replies.length > 0;
        const isReply = isDiscussionView && comment.id !== currentDiscussion?.id;
        
        return (
          <div key={comment.id} className={`flex gap-4 ${isReply ? 'ml-8' : ''}`}>
            {comment.identity_avatar_url ? (
              <img
                src={comment.identity_avatar_url}
                alt={comment.identity_username}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 bg-indigo-100 rounded-full" />
            )}
            <div className="flex-1">
              <div className={`rounded-lg shadow-sm p-4 ${
                isAuthorComment 
                  ? 'bg-indigo-50 border border-indigo-100' 
                  : 'bg-white'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {comment.identity_username}
                    </span>
                    {isAuthorComment && (
                      <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                        Author
                      </span>
                    )}
                    <span className="text-sm text-gray-500">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </span>
                    {comment.updated_at !== comment.created_at && (
                      <span className="text-sm text-gray-500">(edited)</span>
                    )}
                  </div>

                  {(user?.id === comment.user.id || canDeleteComment(comment.user.id)) && (
                    <div className="relative">
                      <button
                        onClick={() => setMenuOpenId(menuOpenId === comment.id ? null : comment.id)}
                        className="p-1 hover:bg-gray-100 rounded-full"
                      >
                        {menuOpenId === comment.id ? (
                          <X className="w-5 h-5 text-gray-500" />
                        ) : (
                          <MoreVertical className="w-5 h-5 text-gray-500" />
                        )}
                      </button>

                      {menuOpenId === comment.id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-10">
                          {user?.id === comment.user.id && (
                            <button
                              onClick={() => handleEdit(comment)}
                              className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-50"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(comment.id)}
                            className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-50 text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {editingId === comment.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      rows={3}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSave(comment.id)}
                        disabled={!editContent.trim()}
                        className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                    
                    {!isDiscussionView && (
                      <div className="mt-4 flex items-center justify-between border-t pt-3">
                        {hasReplies ? (
                          <>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-4 h-4" />
                                {comment.replies.length} replies
                              </span>
                            </div>
                            <Link
                              to={`discussion/${comment.id}`}
                              className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800"
                            >
                              View Discussion
                              <ArrowRight className="w-4 h-4" />
                            </Link>
                          </>
                        ) : (
                          <div className="flex justify-end w-full">
                            <Link
                              to={`discussion/${comment.id}`}
                              className="p-1 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-full transition-colors"
                              title="Reply to comment"
                            >
                              <Reply className="w-5 h-5" />
                            </Link>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}